import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getLanguageById, DEFAULT_LANGUAGE, type LanguageConfig } from '../utils/languageConfig';
import { debounce } from '../lib/utils';
import { downloadAndWarmPyodide } from '../engines/pyodideEngine';

/** Whether the user wants to run Python locally (Pyodide) or via cloud. */
export type PythonMode = 'cloud' | 'downloading' | 'local';

const PYTHON_MODE_KEY = 'playground-python-mode';

export interface TestCase {
  id: string;
  input: string;
  expectedOutput: string;
  passed?: boolean;
}

export interface Problem {
  id: string;
  title: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  description: string;
  examples: Array<{
    input: string;
    output: string;
    explanation?: string;
  }>;
  constraints: string[];
  testCases: TestCase[];
}

interface PlaygroundState {
  code: string;
  language: LanguageConfig;
  stdin: string;
  output: string;
  error: string;
  isRunning: boolean;
  executionTime: string;
  /** Server-measured execution time label (from backend meta), null if unknown */
  serverExecutionTime: string | null;
  fontSize: number;
  /** Which tier ran the last execution ('client' | 'cloud' | null) */
  executionTier: 'client' | 'cloud' | null;
  /** Actual upstream host for cloud runs (codebox|wandbox|godbolt), null otherwise */
  executionProvider: string | null;
  /** Display "via X" badges on run outputs (admin toggle, default true) */
  showExecutionSource: boolean;
  /** Status message shown during execution (e.g. "Loading Python runtime...") */
  statusMessage: string;
  /** Whether to run Python locally (Pyodide) or via cloud */
  pythonMode: PythonMode;
  /** Download/init progress 0–100 while pythonMode === 'downloading' */
  pyodideProgress: number;
  /** Human-readable label for the download step */
  pyodideLabel: string;
}

interface PlaygroundContextType extends PlaygroundState {
  setCode: (code: string) => void;
  setLanguage: (languageId: string) => void;
  setStdin: (stdin: string) => void;
  setOutput: (output: string) => void;
  setError: (error: string) => void;
  setIsRunning: (isRunning: boolean) => void;
  setExecutionTime: (time: string) => void;
  setServerExecutionTime: (time: string | null) => void;
  setExecutionTier: (tier: 'client' | 'cloud' | null) => void;
  setExecutionProvider: (provider: string | null) => void;
  setStatusMessage: (message: string) => void;
  resetCode: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  clearOutput: () => void;
  /** Trigger download + warm-up of Pyodide for local Python execution */
  startLocalPython: () => void;
  /** Revert to cloud execution for Python */
  revertToCloudPython: () => void;
  /** Error message if Pyodide failed to initialize, null otherwise */
  pyodideError: string | null;
  /** Non-null when the running program is waiting for user input */
  inputPrompt: string | null;
  setInputPrompt: (prompt: string | null) => void;
  /** Ref holding the resolve callback for the current input request */
  inputResolverRef: React.MutableRefObject<((value: string) => void) | null>;
}

const PlaygroundContext = createContext<PlaygroundContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'playground-state';
const AUTO_SAVE_DELAY = 2000; // 2 seconds

export function PlaygroundProvider({ children }: { children: ReactNode }) {
  const [inputPrompt, setInputPrompt] = useState<string | null>(null);
  const inputResolverRef = useRef<((value: string) => void) | null>(null);

  const [pythonMode, setPythonMode] = useState<PythonMode>(() => {
    // If user previously enabled local mode, we'll auto-warm in useEffect below
    const saved = localStorage.getItem(PYTHON_MODE_KEY);
    return saved === 'local' ? 'downloading' : 'cloud';
  });
  const [pyodideProgress, setPyodideProgress] = useState(0);
  const [pyodideLabel, setPyodideLabel] = useState('');
  const [pyodideError, setPyodideError] = useState<string | null>(null);

  const [state, setState] = useState<PlaygroundState>(() => {
    // Load from localStorage
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          language: getLanguageById(parsed.languageId || DEFAULT_LANGUAGE),
          isRunning: false,
          output: '',
          error: '',
          executionTime: '',
          serverExecutionTime: null,
          executionTier: null,
          executionProvider: null,
          statusMessage: '',
        };
      } catch (error) {
        console.error('Failed to parse saved state:', error);
      }
    }

    const defaultLanguage = getLanguageById(DEFAULT_LANGUAGE);
    return {
      code: defaultLanguage.boilerplate,
      language: defaultLanguage,
      stdin: '',
      output: '',
      error: '',
      isRunning: false,
      executionTime: '',
      serverExecutionTime: null,
      fontSize: 14,
      executionTier: null,
      executionProvider: null,
      statusMessage: '',
    };
  });

  // Check for snippet loaded from Snippets page
  useEffect(() => {
    const raw = sessionStorage.getItem('load-snippet');
    if (raw) {
      sessionStorage.removeItem('load-snippet');
      try {
        const { language: langId, code } = JSON.parse(raw);
        if (langId && code) {
          const lang = getLanguageById(langId);
          setState((prev) => ({ ...prev, language: lang, code, output: '', error: '' }));
        }
      } catch { /* ignore */ }
    }
  }, []);

  // Auto-save to localStorage
  const saveToLocalStorage = useCallback(
    debounce((stateToSave: PlaygroundState) => {
      try {
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({
            code: stateToSave.code,
            languageId: stateToSave.language.id,
            stdin: stateToSave.stdin,
            fontSize: stateToSave.fontSize,
          })
        );
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
    }, AUTO_SAVE_DELAY),
    []
  );

  useEffect(() => {
    saveToLocalStorage(state);
  }, [state.code, state.language, state.stdin, state.fontSize, saveToLocalStorage]);

  const warmPyodide = useCallback(() => {
    setPythonMode('downloading');
    setPyodideProgress(0);
    setPyodideLabel('Starting download…');
    setPyodideError(null);
    downloadAndWarmPyodide((percent, label) => {
      setPyodideProgress(percent);
      setPyodideLabel(label);
    }).then(() => {
      setPythonMode('local');
      localStorage.setItem(PYTHON_MODE_KEY, 'local');
    }).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to load Python runtime';
      setPyodideError(msg);
      setPythonMode('cloud');
      localStorage.removeItem(PYTHON_MODE_KEY);
    });
  }, []);

  // Auto-warm if user had previously opted for local
  useEffect(() => {
    if (pythonMode === 'downloading') {
      warmPyodide();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount only

  const startLocalPython = useCallback(() => {
    if (pythonMode !== 'cloud') return;
    warmPyodide();
  }, [pythonMode, warmPyodide]);

  const revertToCloudPython = useCallback(() => {
    setPythonMode('cloud');
    localStorage.removeItem(PYTHON_MODE_KEY);
  }, []);

  const setCode = (code: string) => {
    setState((prev) => ({ ...prev, code }));
  };

  const setLanguage = (languageId: string) => {
    const newLanguage = getLanguageById(languageId);
    setState((prev) => ({
      ...prev,
      language: newLanguage,
      code: newLanguage.boilerplate,
      output: '',
      error: '',
    }));
  };

  const setStdin = (stdin: string) => {
    setState((prev) => ({ ...prev, stdin }));
  };

  const setOutput = (output: string) => {
    setState((prev) => ({ ...prev, output }));
  };

  const setError = (error: string) => {
    setState((prev) => ({ ...prev, error }));
  };

  const setIsRunning = (isRunning: boolean) => {
    setState((prev) => ({ ...prev, isRunning }));
  };

  const setExecutionTime = (executionTime: string) => {
    setState((prev) => ({ ...prev, executionTime }));
  };
  const setServerExecutionTime = (serverExecutionTime: string | null) => {
    setState((prev) => ({ ...prev, serverExecutionTime }));
  };

  const setExecutionTier = (executionTier: 'client' | 'cloud' | null) => {
    setState((prev) => ({ ...prev, executionTier }));
  };

  const setExecutionProvider = (executionProvider: string | null) => {
    setState((prev) => ({ ...prev, executionProvider }));
  };

  const [showExecutionSource, setShowExecutionSource] = useState(true);

  // Display flag for "via X" badges (president/super-admin toggle, default on).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = (import.meta.env.VITE_MAIN_API_URL as string | undefined)?.replace(/\/+$/, '');
        if (!base) return;
        const res = await fetch(`${base}/api/settings/public`);
        const json = await res.json();
        if (!cancelled && json?.data && typeof json.data.showExecutionSource === 'boolean') {
          setShowExecutionSource(json.data.showExecutionSource);
        }
      } catch {
        /* offline/error: keep default true */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setStatusMessage = (statusMessage: string) => {
    setState((prev) => ({ ...prev, statusMessage }));
  };

  const resetCode = () => {
    setState((prev) => ({
      ...prev,
      code: prev.language.boilerplate,
      output: '',
      error: '',
      executionTime: '',
      serverExecutionTime: null,
    }));
  };

  const increaseFontSize = () => {
    setState((prev) => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 2, 24),
    }));
  };

  const decreaseFontSize = () => {
    setState((prev) => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 2, 10),
    }));
  };

  const clearOutput = () => {
    setState((prev) => ({
      ...prev,
      output: '',
      error: '',
      executionTime: '',
      executionTier: null,
      executionProvider: null,
      serverExecutionTime: null,
      statusMessage: '',
    }));
  };

  const value: PlaygroundContextType = {
    ...state,
    setCode,
    setLanguage,
    setStdin,
    setOutput,
    setError,
    setIsRunning,
    setExecutionTime,
    setServerExecutionTime,
    setExecutionTier,
    setExecutionProvider,
    showExecutionSource,
    setStatusMessage,
    resetCode,
    increaseFontSize,
    decreaseFontSize,
    clearOutput,
    startLocalPython,
    revertToCloudPython,
    pythonMode,
    pyodideProgress,
    pyodideLabel,
    pyodideError,
    inputPrompt,
    setInputPrompt,
    inputResolverRef,
  };

  return (
    <PlaygroundContext.Provider value={value}>
      {children}
    </PlaygroundContext.Provider>
  );
}

export function usePlayground() {
  const context = useContext(PlaygroundContext);
  if (context === undefined) {
    throw new Error('usePlayground must be used within a PlaygroundProvider');
  }
  return context;
}
